import React, { useEffect, useState } from "react";
import {
  Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, TextField,
} from "@mui/material";
import { actualizarPresupuesto, crearPresupuesto, MESES, num } from "../services/presupuestoService";

const fieldSx = {
  "& .MuiInputBase-root": { minHeight: 32 },
  "& .MuiInputBase-input": { py: "5px", fontSize: "0.8rem" },
  "& .MuiInputLabel-root": { fontSize: "0.8rem" },
};

const vacio = (anio) => ({
  nombre: "", concepto: "", id_macrocuenta: 0, cuenta: "", nro_contrato: "", moneda: "Bolivianos", anio, observacion: "",
  ...Object.fromEntries(MESES.map((m) => [m, "0"])),
});

// Alta (todos los datos) o edición (sólo observación y montos por mes) de un presupuesto.
export default function PresupuestoFormDialog({ abierto, fila, anio, catalogos, onClose, onGuardado }) {
  const edita = !!fila;
  const [f, setF] = useState(vacio(anio));
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!abierto) return;
    setError("");
    if (fila) {
      setF({ ...vacio(anio), ...fila, observacion: fila.Observacion ?? fila.observacion ?? "", ...Object.fromEntries(MESES.map((m) => [m, String(num(fila[m]))])) });
    } else {
      setF(vacio(anio));
    }
  }, [abierto, fila, anio]);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const elegirConcepto = (e) => {
    const c = catalogos.conceptos.find((x) => x.concepto === e.target.value);
    setF((p) => ({ ...p, concepto: e.target.value, id_macrocuenta: c?.id_macrocuenta ?? 0, cuenta: c ? String(c.cuenta) : "" }));
  };

  const guardar = async () => {
    setError("");
    const meses = Object.fromEntries(MESES.map((m) => [m, num(f[m])]));
    if (!edita && (!String(f.nombre).trim() || !String(f.concepto).trim() || !Number(f.anio))) {
      setError("Faltan campos obligatorios (nombre, concepto y año).");
      return;
    }
    setGuardando(true);
    try {
      if (edita) {
        await actualizarPresupuesto(fila.id, { observacion: f.observacion.trim(), ...meses });
      } else {
        await crearPresupuesto({
          nombre: f.nombre.trim(), concepto: f.concepto.trim(), id_macrocuenta: Number(f.id_macrocuenta) || 0, cuenta: String(f.cuenta).trim(),
          nro_contrato: String(f.nro_contrato).trim(), moneda: f.moneda, anio: Number(f.anio), observacion: f.observacion.trim(), ...meses,
        });
      }
      onGuardado(edita ? "Presupuesto actualizado." : "Presupuesto creado.");
      onClose();
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.message || "No se pudo guardar el presupuesto.");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={abierto} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ py: 1, fontSize: "0.95rem", fontWeight: 900 }}>
        {edita ? `Editar presupuesto — ${fila.nombre} / ${fila.concepto}` : "Adicionar presupuesto"}
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 1, py: 0 }}>{error}</Alert>}
        <Grid container spacing={1}>
          {!edita && (
            <>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField select fullWidth size="small" label="Nombre" value={f.nombre} onChange={set("nombre")} sx={fieldSx}>
                  {catalogos.nombres.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField select fullWidth size="small" label="Concepto" value={f.concepto} onChange={elegirConcepto} sx={fieldSx}>
                  {catalogos.conceptos.map((c) => <MenuItem key={c.id} value={c.concepto}>{c.concepto}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 6, md: 2 }}><TextField fullWidth size="small" label="Contrato" value={f.nro_contrato} onChange={set("nro_contrato")} sx={fieldSx} /></Grid>
              <Grid size={{ xs: 6, md: 2 }}>
                <TextField select fullWidth size="small" label="Moneda" value={f.moneda} onChange={set("moneda")} sx={fieldSx}>
                  <MenuItem value="Bolivianos">Bolivianos</MenuItem>
                </TextField>
              </Grid>
              <Grid size={{ xs: 6, md: 2 }}><TextField fullWidth size="small" type="number" label="Año" value={f.anio} onChange={set("anio")} sx={fieldSx} /></Grid>
              <Grid size={{ xs: 6, md: 3 }}><TextField fullWidth size="small" label="Macrocuenta (automática)" value={f.id_macrocuenta || ""} disabled sx={fieldSx} /></Grid>
              <Grid size={{ xs: 6, md: 3 }}><TextField fullWidth size="small" label="Cuenta (automática)" value={f.cuenta} disabled sx={fieldSx} /></Grid>
            </>
          )}
          <Grid size={12}><TextField fullWidth size="small" label="Observación" value={f.observacion} onChange={set("observacion")} multiline minRows={1} sx={fieldSx} /></Grid>
          {MESES.map((m) => (
            <Grid key={m} size={{ xs: 6, md: 3 }}>
              <TextField fullWidth size="small" type="number" label={m} value={f[m]} onChange={set(m)} slotProps={{ htmlInput: { step: "0.01" } }} sx={fieldSx} />
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 0.75 }}>
        <Button size="small" onClick={onClose}>Cancelar</Button>
        <Button size="small" variant="contained" disabled={guardando} onClick={guardar}>
          {guardando ? <CircularProgress size={16} color="inherit" /> : "Guardar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
